import { journalBoundaryContext, boundaryValue } from '../ability/boundary-journal.js';
import { activeStatuses, ecId, ecLean, record, type JanusCtx, type Row } from '../ability/context';
import { runTriggerFamily } from '../ability/trigger-executor';
import { effectAttempt } from '../ability/boundary-checkpoint';
import { abilityChargeEntry } from '../ability/charges/charge-entry';
import { adjustCharges } from '../ability/charges/adjust-charges';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers';
import { statusItemId } from '../statuses/expiry';
import { collectTriggerCandidates } from '../reactions/trigger-dispatch';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers';
import { statusScalar } from '../actions/activity';
import { LAST_STAND_ROLL_TYPES as HEAL_TYPES } from './last-stand-roll-types';
import { chargeReductionDelta } from '../resources/reduction-guard';
import { logPreventedReduction, resourceReductionReaders } from '../ability/resource-reduction';

const name = (row: Row): string => String(ecLean(row, 'encounter_creature_name') ?? 'Creature');

/** The vitality-restoring self-zero arm precedes death; a successful ward saves the victim. */
export function tryLastStand(ctx: JanusCtx, target: Row, caster: Row): boolean {
    if (ctx.dryRun) return false;
    const event = { trigger: 'triggers_on_self_zero_vitality', restrictToAbilityRollTypes: HEAL_TYPES, statusIsCharge: true };
    const due = collectTriggerCandidates(janusCtxTriggerReaders(ctx), target, event);
    if (!due.carriers.length && !due.statuses.length) return false;
    ctx = journalBoundaryContext(ctx); target = boundaryValue(ctx, target); caster = boundaryValue(ctx, caster);
    effectAttempt(ctx, `${name(target)}: last stand`, () => {
        const fired = runTriggerFamily(ctx, event, target, caster, [target], false);
        if (Number(ecLean(target, 'v_current') ?? 0) <= 0) return;
        for (const candidate of fired) {
            if (Number(ctx.read.value(candidate.ability, 'ability_charges') ?? 0) < 1) continue;
            if (candidate.pool === 'status') {
                if (chargeReductionDelta(target, -1, resourceReductionReaders(ctx)) === 0) {
                    logPreventedReduction(ctx, target, 'charges');
                    continue;
                }
                const status = activeStatuses(ctx, target).find(row => candidate.statusId
                    ? String(statusScalar(row, 'status_id')) === candidate.statusId : statusItemId(row) === candidate.abilityId);
                if (status) removeStoredStatus(ctx, target, status, 'single');
            } else {
                const pool = abilityChargeEntry(ctx, target, candidate.abilityId);
                // Trigger dispatch can debit an authored activation cost itself.
                if (pool.current > 0 && !ctx.chargesChanged.some(change => change.holder === ecId(target)
                    && change.item_id === candidate.abilityId && change.charges < change.before))
                    adjustCharges(ctx, target, candidate.abilityId, -1, true);
            }
        }
    });
    return Number(ecLean(target, 'v_current') ?? record(record(target.computed)?.vitality)?.v_current ?? 0) > 0;
}

/** Non-healing self-zero and death listeners are due only after the actual terminal outcome. */
export function actualDeathTriggers(ctx: JanusCtx, target: Row, caster: Row): void {
    if (ctx.dryRun) return;
    for (const trigger of ['triggers_on_self_zero_vitality', 'triggers_on_death']) {
        if (trigger === 'triggers_on_death' && Number(ecLean(target, 'encounter_creature_dead') ?? 0) !== 1) continue;
        const event = { trigger, ...(trigger === 'triggers_on_self_zero_vitality' ? { excludeAbilityRollTypes: HEAL_TYPES } : {}) };
        const due = collectTriggerCandidates(janusCtxTriggerReaders(ctx), target, event);
        if (!due.carriers.length && !due.statuses.length) continue;
        ctx = journalBoundaryContext(ctx); target = boundaryValue(ctx, target); caster = boundaryValue(ctx, caster);
        effectAttempt(ctx, `${name(target)}: ${trigger}`, () => { runTriggerFamily(ctx, event, target, caster, [target], false); });
    }
}
